import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getDealflowDebug } from "../api/dealflowApi";

export default function DebugDrawer({ vertical, country, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dealflow", "debug", vertical, country || ""],
    queryFn: () => getDealflowDebug({ vertical, country, sample_size: 5 }),
  });

  return (
    <div
      data-testid="debug-drawer"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-zinc-950 border-l border-zinc-800 h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950">
          <h2 className="text-zinc-100 text-lg">DealFlow debug — {vertical}</h2>
          <button
            onClick={onClose}
            data-testid="debug-drawer-close"
            className="text-zinc-400 hover:text-zinc-100 px-3 py-1"
          >
            ×
          </button>
        </div>
        <div className="p-5 space-y-6">
          {isLoading && <div className="text-zinc-500">Loading...</div>}
          {error && (
            <div className="text-rose-400">Error: {String(error.message || error)}</div>
          )}
          {data && (
            <>
              <Section title="Applied filters">
                <Kv data={data.applied_filters} fallback="None" />
              </Section>
              <Section title="Disabled filters">
                {data.disabled_filters.length === 0 ? (
                  <div className="text-zinc-500">None</div>
                ) : (
                  <ul className="text-zinc-300 list-disc ml-5">
                    {data.disabled_filters.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                )}
              </Section>
              <Section title="Funnel">
                <FunnelBars funnel={data.funnel} />
              </Section>
              <Section title="Exclusions">
                <Kv data={data.exclusions} />
              </Section>
              <Section title="Sample excluded">
                {data.samples.excluded_ids.length === 0 ? (
                  <div className="text-zinc-500">No excluded samples</div>
                ) : (
                  <ul className="text-xs space-y-2">
                    {data.samples.excluded_ids.slice(0, 15).map((s, i) => (
                      <li
                        key={`${s.ulid}-${i}`}
                        className="text-zinc-300 border-l-2 border-amber-500/40 pl-3"
                      >
                        <a
                          href={`/listing/${s.ulid}`}
                          className="text-amber-300 hover:underline"
                        >
                          {s.ulid}
                        </a>{" "}
                        — <span className="text-zinc-400">{s.reason}</span>
                        {s.value != null && (
                          <span className="text-zinc-500">
                            {" "}
                            (value: {JSON.stringify(s.value)})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
              <Section title="Breakdowns">
                <pre className="text-xs text-zinc-400 bg-zinc-900 rounded p-3 overflow-x-auto">
                  {JSON.stringify(data.breakdowns, null, 2)}
                </pre>
              </Section>
              <Section title="Timing (ms)">
                <Kv data={data.timing_ms} />
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
        {title}
      </h3>
      <div>{children}</div>
    </section>
  );
}

function Kv({ data, fallback = null }) {
  const entries = data && typeof data === "object" ? Object.entries(data) : [];
  if (entries.length === 0)
    return <div className="text-zinc-500">{fallback || "—"}</div>;
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-zinc-500">{k}</dt>
          <dd className="text-zinc-200 text-right tabular-nums">
            {typeof v === "object" ? JSON.stringify(v) : String(v)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function FunnelBars({ funnel }) {
  const entries = Object.entries(funnel || {});
  if (entries.length === 0) return null;
  const max = Math.max(1, ...entries.map(([, v]) => Number(v) || 0));
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => {
        const w = `${Math.round((Number(v) / max) * 100)}%`;
        return (
          <div key={k} className="text-xs">
            <div className="flex justify-between text-zinc-400 mb-0.5">
              <span>{k}</span>
              <span className="tabular-nums">{v}</span>
            </div>
            <div className="h-1.5 bg-zinc-800 rounded overflow-hidden">
              <div
                className="h-full bg-amber-500/60"
                style={{ width: w }}
              ></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
